/**
 * TypeScript interfaces for Visit Data and QA API responses
 */

import type { MedicalData } from './medicalData.types';

// Visit Data response type
export interface VisitDataResponse {
  visit_id: string;
  agency_id: string;
  visit_type: string;
  transcript: string;
  bulleted_transcript_chunks: string[];
  medication_transcript: string;
  hnp_summary: string;
  patient_id: string;
  patient_first_name: string;
  patient_last_name: string;
  nurse_username: string;
  visit_date: string;
  pass_through_existing_answers: boolean;
  form_answer_state: Record<string, unknown>;
}

// QA Correction types
export interface QACorrection {
  rule_name?: string;
  original_entry?: string;
  ai_corrected_entry?: string;
  emr_corrections?: Array<{
    question_id?: string;
    ai_corrected_answer_id?: string;
    ai_corrected_answer_value?: string;
  }>;
  justification?: string;
  oasis_form_id?: string;
  confidence_score?: number;
  severity?: string;
  sources?: string[];
}

// QA Data types
export interface QATimingData {
  start_time: string; // ISO datetime string
  end_time: string | null; // ISO datetime string or null
  duration_ms: number | null; // duration in milliseconds or null
}

export interface QACostData {
  name: string;
  cost_usd: number;
  input_tokens?: number;
  output_tokens?: number;
}

export interface QADataResponse {
  qa_id: string;
  status: string;
  total_corrections: number;
  scrape_datetime: string;
  created_at: string;
  agency_id?: string;
  qa_corrections_by_rule?: QACorrection[];
  use_structured_qa_rules?: boolean;
  medical_data?: MedicalData;
  bulleted_transcript_chunks?: Record<string, string>;
  timings?: Record<string, QATimingData>;
  cost?: QACostData[];
  total_cost?: number;
}

export type QADataItem = Pick<
  QADataResponse,
  'qa_id' | 'status' | 'created_at' | 'total_corrections'
>;

export interface QAVisit {
  visit_id: string;
  patient_id: string;
  patient_first_name: string;
  patient_last_name: string;
  visit_date?: string;
  visit_type: string;
  nurse_username: string;
}

export interface QAVisitWithNonStandardDateKey extends QAVisit {
  visit_date_date?: string;
  qa_data?: QADataItem[];
}

// QA Status Constants
export const QA_STATUS = {
  NOT_STARTED: 'NOT_STARTED',
  INITIALIZED: 'INITIALIZED',
  PATIENT_INFO_SET: 'PATIENT_INFO_SET',
  QA_ANALYSIS_GENERATED: 'QA_ANALYSIS_GENERATED',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
  CANCELLED: 'CANCELLED',
} as const;

export type QAStatus = (typeof QA_STATUS)[keyof typeof QA_STATUS];
